/**
 * useHarvestBatches Hook
 *
 * Fetches production batches available for harvest recording.
 * Returns separate lists for legacy batches and standard harvestable batches.
 *
 * Used by UnifiedHarvestForm to populate the batch dropdown.
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'

// Statuses that indicate a batch is ready for harvest
const HARVESTABLE_STATUSES = ['FRUITING', 'FIRST_FLUSH', 'RESTING', 'SECOND_FLUSH']

/**
 * Hook to fetch batches available for harvest recording
 * @returns {Object} { legacyBatches, standardBatches, loading, error, refetch }
 */
export function useHarvestBatches() {
  const [legacyBatches, setLegacyBatches] = useState([])
  const [standardBatches, setStandardBatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchBatches = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Fetch all batches that are either legacy or in harvestable status
      const { data, error: fetchError } = await supabase
        .from('production_batches')
        .select(`
          id,
          batch_code,
          is_legacy,
          current_state,
          total_blocks,
          total_harvest_kg,
          inoculation_date,
          strain:strains (
            id,
            code,
            name
          )
        `)
        .or(`is_legacy.eq.true,current_state.in.(${HARVESTABLE_STATUSES.join(',')})`)
        .order('inoculation_date', { ascending: false })

      if (fetchError) {
        throw fetchError
      }

      // Separate into legacy and standard batches
      const legacy = []
      const standard = []

      for (const batch of data || []) {
        if (batch.is_legacy) {
          legacy.push(batch)
        } else if (HARVESTABLE_STATUSES.includes(batch.current_state)) {
          standard.push(batch)
        }
      }

      setLegacyBatches(legacy)
      setStandardBatches(standard)
    } catch (err) {
      console.error('[useHarvestBatches] Error:', err)
      setError(err.message)
      setLegacyBatches([])
      setStandardBatches([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchBatches()
  }, [fetchBatches])

  return {
    legacyBatches,
    standardBatches,
    loading,
    error,
    refetch: fetchBatches
  }
}

export default useHarvestBatches
